import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from "@mui/material";
import { useEffect, useState } from "react";
import { FaBuildingUser } from "react-icons/fa6";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { CompanyInterface } from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import CreateButton from "../../components/CreateButton";
import CustomTooltip from "../../components/CustomTooltip";
import {
  TypeActiveStatus,
  TypeActiveStatusLabel
} from "../../components/enum/typeActiveStatusEnum";
import CompanyCreateModal from "../../components/modal/CompanyCreateModal";
import PaginationControls from "../../components/PaginationControls";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { TextFeldUserSearch } from "../users/Users.styles";
import UserStatusSwitch from "../users/UserStatusSwitch";
import {
  ActionButton,
  ActionsCell,
  CompanySearchCard,
  Container,
  Table,
  TableContainer,
  Title
} from "./company.styled";

export const Company = () => {
  const [companies, setCompanies] = useState<CompanyInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<number | null>(null);
  const [companyNameToDelete, setCompanyNameToDelete] = useState<string | null>(
    null
  );
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyInterface | null>(null);
  const [haveCompany, setHaveCompany] = useState(false);
  const [search, setSearch] = useState<string>("");

  const [companyStatus, setCompanytatus] = useState<TypeActiveStatus>(
    TypeActiveStatus.NONE
  );

  const { isUser, isAdmin, user } = useAuth();

  const containerId = "toaster-company-page";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const fetchCompanies = async (currentPage: number) => {
    let current_status;

    if (companyStatus === TypeActiveStatus.ACTIVE) {
      current_status = true;
    } else if (companyStatus === TypeActiveStatus.INACTIVE) {
      current_status = false;
    } else {
      current_status = undefined;
    }

    try {
      const response = await api.get(`/company`, {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          showActives: current_status
        }
      });

      if (response.data.items.length > 0) {
        setHaveCompany(true);
      } else {
        setHaveCompany(false);
      }

      setCompanies(response.data.items);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
      handleHttpError(error);
    }
  };

  const openDeleteDialog = (company: CompanyInterface) => {
    setCompanyToDelete(company.company_id);
    setCompanyNameToDelete(company.company_name);
    setDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDialogOpen(false);
    setCompanyToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (companyToDelete !== null) {
      try {
        await api.delete(`/company/${companyToDelete}`);
        autoSuccess(`Empresa deletada com sucesso!`);
        fetchCompanies(page);
      } catch (error) {
        console.error("Erro ao deletar empresa:", error);
        handleHttpError(error);
      } finally {
        setDialogOpen(false);
        setCompanyToDelete(null);
      }
    }
  };

  const handleEdit = async (company_id: number) => {
    try {
      const response = await api.get(`/company/${company_id}`);
      setSelectedCompany(response.data);
      setIsModalOpen(true);
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao buscar empresa:", error);
      alert(msg);
    }
  };

  const handleClear = () => {
    setSearch("");
    setPage(1);
  };

  useEffect(() => {
    fetchCompanies(page);
  }, [page, search, companyStatus]);

  useEffect(() => {
    handleClear();
  }, [isModalOpen]);

  const handleToggleStatus = async (
    company_id: number,
    company_status: boolean
  ) => {
    try {
      const newStatus = !company_status;
      await api.patch(`/company/${company_id}`, { status: newStatus });

      if (newStatus) {
        autoSuccess("Empresa ativada com sucesso!");
      } else {
        autoSuccess("Empresa desativada com sucesso!");
      }

      fetchCompanies(page); // Atualiza a lista
    } catch (error) {
      handleHttpError(error);
      console.error("Erro ao atualizar status do usuário:", error);
    }
  };

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Lista de Empresas
        {!haveCompany ||
          (isAdmin && (
            <CreateButton
              onClick={() => setIsModalOpen(true)}
              style={{ marginLeft: "14px" }}
            >
              <FaBuildingUser size={16} /> Criar Empresa
            </CreateButton>
          ))}
      </Title>

      {isAdmin && (
        <CompanySearchCard>
          <TextFeldUserSearch
            id="user_search"
            label="Dados da Empresa"
            variant="outlined"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1); // reseta para primeira página ao buscar
            }}
          />

          <FormControl>
            <InputLabel id="label-status">Status</InputLabel>
            <Select
              sx={{ width: "200px" }}
              labelId="label-status"
              value={companyStatus}
              label="Status"
              onChange={(e) =>
                setCompanytatus(e.target.value as TypeActiveStatus)
              }
            >
              {(Object.values(TypeActiveStatus) as TypeActiveStatus[])
                // .filter(
                //     (code) => code !== TypeActiveStatus.NONE
                // )
                .map((statusCode) => (
                  <MenuItem key={statusCode} value={statusCode}>
                    {TypeActiveStatusLabel[statusCode]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </CompanySearchCard>
      )}

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>CNPJ</th>
              <th>Status</th>
              <th>Criado em</th>
              {!isUser && <th>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <tr key={company.company_id}>
                <td>{company.company_name}</td>
                <td>{company.company_cnpj}</td>
                <td>{company.company_status ? "Ativa" : "Inativa"}</td>
                <td>
                  {new Date(company.created_at).toLocaleDateString("pt-BR")}
                </td>

                {!isUser && (
                  <ActionsCell>
                    {isAdmin && (
                      <UserStatusSwitch
                        checked={company.company_status as any}
                        onToggle={() =>
                          handleToggleStatus(
                            company.company_id,
                            company.company_status as any
                          )
                        }
                      />
                    )}

                    <CustomTooltip title="Editar Empresa">
                      <ActionButton
                        onClick={() => handleEdit(company.company_id)}
                      >
                        <FiEdit size={20} color="#4caf50" />
                      </ActionButton>
                    </CustomTooltip>

                    {isAdmin && (
                      <CustomTooltip title="Excluir Empresa">
                        <ActionButton onClick={() => openDeleteDialog(company)}>
                          <FiTrash2 size={20} color="#f44336" />
                        </ActionButton>
                      </CustomTooltip>
                    )}
                  </ActionsCell>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <Dialog open={dialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir {companyNameToDelete}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <CompanyCreateModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCompany(null);
        }}
        onCompanyCreated={() => fetchCompanies(page)}
        company={selectedCompany}
      />
    </Container>
  );
};
